import type { CSSProperties, ReactNode } from 'react'
import { cn } from './cn'

/**
 * The product visuals' building blocks: a browser frame, app chrome, status pills and a
 * status pill that turns from one tone to another on a loop. All server-rendered, all
 * illustrative, and every figure they show is invented.
 */

/**
 * A browser window drawn in CSS. Three nested wrappers, in this order: `[data-parallax]`
 * (reserved for a scroll drift; must carry no CSS transform), the tilt wrapper (takes the
 * caller's `tilt` / `tilt-reverse`; `isolate` keeps the glow behind the frame), and the
 * frame itself, which always casts the shadow. The title bar is a three-track grid so the
 * address pill centres against the frame, not against the space left by the lights.
 *
 * `aria-hidden`: the chrome is decoration around content that is itself decoration.
 */
export function DeviceFrame({
  children,
  label = 'wysehub.co.uk',
  className,
  padded = true,
}: {
  readonly children: ReactNode
  readonly label?: string
  readonly className?: string
  readonly padded?: boolean
}) {
  return (
    <div className="device-frame-wrap" data-parallax>
      <div className={cn('device-frame-wrap isolate', className)}>
        <span aria-hidden="true" className="device-glow" />
        <div
          aria-hidden="true"
          className="device-frame overflow-hidden rounded-card border border-line bg-surface"
        >
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-line bg-surface-muted px-4 py-3">
            <span className="flex gap-1.5">
              <span className="size-2.5 rounded-full bg-line-strong opacity-60" />
              <span className="size-2.5 rounded-full bg-line-strong opacity-60" />
              <span className="size-2.5 rounded-full bg-line-strong opacity-60" />
            </span>
            <span className="rounded-full bg-surface px-3 py-0.5 text-xs text-ink-muted shadow-lift-sm">
              {label}
            </span>
            <span />
          </div>
          <div className={cn(padded && 'p-4 sm:p-5')}>{children}</div>
        </div>
      </div>
    </div>
  )
}

/** The portal's chrome around a visual: a slim sidebar, a top bar with a crumb. */
export function AppChrome({
  children,
  crumb,
}: {
  readonly children: ReactNode
  readonly crumb: string
}) {
  return (
    <div className="flex bg-surface">
      <aside className="hidden w-12 shrink-0 flex-col items-center gap-3 border-r border-line bg-surface-muted py-3 sm:flex">
        <span className="app-chrome-mark grid size-7 place-items-center rounded-lg bg-brand font-display text-xs font-black text-brand-foreground">
          W
        </span>
        {[0, 1, 2, 3].map((row) => (
          <span
            className={cn(
              'h-1.5 w-6 rounded-full',
              row === 0 ? 'bg-brand/50' : 'bg-line',
            )}
            key={row}
          />
        ))}
      </aside>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2">
          <span className="truncate text-[11px] text-ink-muted">{crumb}</span>
          <span className="size-5 shrink-0 rounded-full bg-brand-subtle ring-1 ring-brand/30" />
        </div>
        <div className="p-4 sm:p-5">{children}</div>
      </div>
    </div>
  )
}

export type Tone = 'success' | 'failure' | 'running' | 'unknown'

const TONE_CLASSES: Record<Tone, string> = {
  success: 'border-success/40 bg-success-subtle text-ink',
  failure: 'border-danger/40 bg-danger-subtle text-danger',
  running: 'border-warning/40 bg-warning-subtle text-warning',
  unknown: 'border-line-strong bg-surface-muted text-ink-muted',
}

/** A short status pill, coloured by tone; the label is always present. */
export function StatusBadge({
  tone,
  label,
}: {
  readonly tone: Tone
  readonly label: string
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium',
        TONE_CLASSES[tone],
      )}
    >
      {label}
    </span>
  )
}

const TONE_VARS: Record<Tone, { bg: string; line: string; ink: string }> = {
  success: {
    bg: 'var(--color-success-subtle)',
    line: 'color-mix(in oklab, var(--color-success) 40%, transparent)',
    ink: 'var(--color-ink)',
  },
  failure: {
    bg: 'var(--color-danger-subtle)',
    line: 'color-mix(in oklab, var(--color-danger) 40%, transparent)',
    ink: 'var(--color-danger)',
  },
  running: {
    bg: 'var(--color-warning-subtle)',
    line: 'color-mix(in oklab, var(--color-warning) 40%, transparent)',
    ink: 'var(--color-warning)',
  },
  unknown: {
    bg: 'var(--color-surface-muted)',
    line: 'var(--color-line-strong)',
    ink: 'var(--color-ink-muted)',
  },
}

/**
 * A status pill that advances from one tone to another, on a loop, in CSS alone. With no
 * animation running it rests on the tone it ends on, which is the state a finished screen
 * should be in. The label widths are estimated in `em`, because CSS cannot measure text.
 */
export function AnimatedStatus({
  from,
  to,
  fromLabel,
  toLabel,
  loopIndex = 0,
}: {
  readonly from: Tone
  readonly to: Tone
  readonly fromLabel: string
  readonly toLabel: string
  readonly loopIndex?: number
}) {
  const a = TONE_VARS[from]
  const b = TONE_VARS[to]
  const style = {
    '--s-a-bg': a.bg,
    '--s-a-line': a.line,
    '--s-a-ink': a.ink,
    '--s-b-bg': b.bg,
    '--s-b-line': b.line,
    '--s-b-ink': b.ink,
    '--loop-index': loopIndex,
    // Estimated label widths for the width animation; CSS cannot measure text. Both are
    // `max-width` in the stylesheet, so a pill is always bound by its content and an
    // estimate only binds mid-transition. Generous for the system face, which is wider
    // than WyseAi's Google Sans: 0.8em a character to leave from, 0.7em to rest at.
    '--w-from': `calc(${fromLabel.length} * 0.8em + 1.4rem)`,
    '--w-to': `calc(${toLabel.length} * 0.7em + 1.4rem)`,
  } as CSSProperties

  return (
    <span
      className="status-cycle shrink-0 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium"
      // biome-ignore lint/plugin: the keyframes read these custom properties; there is no class form
      style={style}
    >
      <span className="status-cycle-from">{fromLabel}</span>
      <span className="status-cycle-to">{toLabel}</span>
    </span>
  )
}
